import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon, CheckCircleIcon, ArrowPathIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { Course } from '../../types/course';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface CourseFormProps {
  isOpen: boolean;
  onClose: () => void;
  onCourseCreated: () => void;
  initialCourse?: Course;
  isEditMode?: boolean;
}

interface ModuleFormData {
  id?: number;
  title: string;
  description: string;
  order: number;
  lessons: LessonFormData[];
}

interface LessonFormData {
  id?: number;
  title: string;
  video_url: string;
  transcript: string;
  summary: string;
  personal_notes: string;
  quiz: string;
  order: number;
}

const CourseForm: React.FC<CourseFormProps> = ({
  isOpen,
  onClose,
  onCourseCreated,
  initialCourse,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState<Partial<Course>>({
    title: '',
    source: '',
    description: '',
    status: 'in_progress',
  });
  const [modules, setModules] = useState<ModuleFormData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (initialCourse) {
      setFormData({
        title: initialCourse.title,
        source: initialCourse.source,
        description: initialCourse.description || '',
        status: initialCourse.status,
        started_at: initialCourse.started_at,
        completed_at: initialCourse.completed_at,
        category: initialCourse.category,
        business: initialCourse.business,
      });
      setModules(initialCourse.modules.map(module => ({
        id: module.id,
        title: module.title,
        description: module.description || '',
        order: module.order,
        lessons: module.lessons.map(lesson => ({
          id: lesson.id,
          title: lesson.title,
          video_url: lesson.video_url || '',
          transcript: lesson.transcript || '',
          summary: lesson.summary || '',
          personal_notes: lesson.personal_notes || '',
          quiz: typeof lesson.quiz === 'string' ? lesson.quiz : '',
          order: lesson.order,
        })),
      })));
    } else {
      setFormData({
        title: '',
        source: '',
        description: '',
        status: 'in_progress',
      });
      setModules([]);
    }
  }, [initialCourse]);

  const handleAddModule = () => {
    setModules([
      ...modules,
      {
        title: '',
        description: '',
        order: modules.length + 1,
        lessons: [],
      },
    ]);
  };

  const handleRemoveModule = (index: number) => {
    setModules(modules.filter((_, i) => i !== index));
  };

  const handleModuleChange = (index: number, field: keyof ModuleFormData, value: string | number) => {
    const newModules = [...modules];
    newModules[index] = {
      ...newModules[index],
      [field]: value,
    };
    setModules(newModules);
  };

  const handleAddLesson = (moduleIndex: number) => {
    const newModules = [...modules];
    newModules[moduleIndex].lessons.push({
      title: '',
      video_url: '',
      transcript: '',
      summary: '',
      personal_notes: '',
      quiz: '',
      order: newModules[moduleIndex].lessons.length + 1,
    });
    setModules(newModules);
  };

  const handleRemoveLesson = (moduleIndex: number, lessonIndex: number) => {
    const newModules = [...modules];
    newModules[moduleIndex].lessons = newModules[moduleIndex].lessons.filter((_, i) => i !== lessonIndex);
    setModules(newModules);
  };

  const handleLessonChange = (moduleIndex: number, lessonIndex: number, field: keyof LessonFormData, value: string | number) => {
    const newModules = [...modules];
    newModules[moduleIndex].lessons[lessonIndex] = {
      ...newModules[moduleIndex].lessons[lessonIndex],
      [field]: value,
    };
    setModules(newModules);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const courseData = {
        ...formData,
        modules: modules.map(module => ({
          ...module,
          lessons: module.lessons,
        })),
      };

      if (isEditMode && initialCourse) {
        await apiClient.put(`/api/courses/${initialCourse.id}/`, courseData);
      } else {
        await apiClient.post('/api/courses/', courseData);
      }
      setSuccess(true);
      onCourseCreated();
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 500);
    } catch (error) {
      console.error('Error saving course:', error);
      setError('Failed to save course');
    } finally {
      setLoading(false);
    }
  };

  // Reusable styles
  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.secondary,
  };

  const inputStyle: React.CSSProperties = {
    marginTop: SPACING[4],
    display: 'block',
    width: '100%',
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.border.default}`,
    padding: `${SPACING[8]} ${SPACING[12]}`,
    fontSize: TYPOGRAPHY.size.sm,
    fontFamily: TYPOGRAPHY.body.fontFamily,
    color: COLORS.text.primary,
    backgroundColor: COLORS.bg.panel,
    outline: 'none',
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    resize: 'vertical',
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 10 }} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(107, 114, 128, 0.75)',
            transition: 'opacity 0.3s ease-out',
          }} />
        </Transition.Child>

        <div style={{ position: 'fixed', inset: 0, zIndex: 10, overflowY: 'auto' }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center',
          }}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel style={{
                position: 'relative',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: RADIUS,
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all 0.3s ease-out',
                width: '100%',
                maxWidth: '896px',
              }}>
                <div style={{ position: 'absolute', right: 0, top: 0, paddingRight: SPACING[16], paddingTop: SPACING[16] }}>
                  <button
                    type="button"
                    style={{
                      borderRadius: RADIUS,
                      backgroundColor: COLORS.bg.panel,
                      color: COLORS.text.tertiary,
                      border: 'none',
                      cursor: 'pointer',
                      padding: SPACING[4],
                    }}
                    onClick={onClose}
                  >
                    <span style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>Close</span>
                    <XMarkIcon style={{ height: '24px', width: '24px' }} aria-hidden="true" />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div style={{ marginTop: 0, textAlign: 'left', width: '100%' }}>
                    <Dialog.Title as="h3" style={{
                      fontSize: TYPOGRAPHY.headings.sizes.h2,
                      fontFamily: TYPOGRAPHY.headings.fontFamily,
                      fontWeight: TYPOGRAPHY.headings.weight,
                      lineHeight: 1.5,
                      color: COLORS.text.primary,
                    }}>
                      {isEditMode ? 'Edit Course' : 'New Course'}
                    </Dialog.Title>
                    <div style={{ marginTop: SPACING[16] }}>
                      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                        {/* Course Details */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                          <div>
                            <label htmlFor="title" style={labelStyle}>
                              Title
                            </label>
                            <input
                              type="text"
                              name="title"
                              id="title"
                              value={formData.title}
                              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                              style={inputStyle}
                              required
                            />
                          </div>

                          <div>
                            <label htmlFor="source" style={labelStyle}>
                              Source
                            </label>
                            <input
                              type="text"
                              name="source"
                              id="source"
                              value={formData.source}
                              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                              style={inputStyle}
                              required
                            />
                          </div>

                          <div>
                            <label htmlFor="description" style={labelStyle}>
                              Description
                            </label>
                            <textarea
                              name="description"
                              id="description"
                              value={formData.description}
                              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                              rows={3}
                              style={textareaStyle}
                            />
                          </div>

                          <div>
                            <label htmlFor="status" style={labelStyle}>
                              Status
                            </label>
                            <select
                              name="status"
                              id="status"
                              value={formData.status}
                              onChange={(e) => setFormData({ ...formData, status: e.target.value as Course['status'] })}
                              style={inputStyle}
                            >
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                              <option value="paused">Paused</option>
                            </select>
                          </div>
                        </div>

                        {/* Modules */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h4 style={{
                              fontSize: TYPOGRAPHY.headings.sizes.h2,
                              fontFamily: TYPOGRAPHY.headings.fontFamily,
                              fontWeight: TYPOGRAPHY.weight.medium,
                              color: COLORS.text.primary,
                            }}>Modules</h4>
                            <button
                              type="button"
                              onClick={handleAddModule}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: `${SPACING[8]} ${SPACING[12]}`,
                                border: 'none',
                                fontSize: TYPOGRAPHY.size.sm,
                                fontWeight: TYPOGRAPHY.weight.medium,
                                borderRadius: RADIUS,
                                color: COLORS.bg.panel,
                                backgroundColor: COLORS.accent.default,
                                cursor: 'pointer',
                              }}
                            >
                              <PlusIcon style={{ marginLeft: '-4px', marginRight: SPACING[8], height: '16px', width: '16px' }} />
                              Add Module
                            </button>
                          </div>

                          {modules.map((module, moduleIndex) => (
                            <div key={moduleIndex} style={{
                              border: `1px solid ${COLORS.border.default}`,
                              borderRadius: RADIUS,
                              padding: SPACING[16],
                              display: 'flex',
                              flexDirection: 'column',
                              gap: SPACING[16],
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                                  <div>
                                    <label style={labelStyle}>
                                      Module Title
                                    </label>
                                    <input
                                      type="text"
                                      value={module.title}
                                      onChange={(e) => handleModuleChange(moduleIndex, 'title', e.target.value)}
                                      style={inputStyle}
                                      required
                                    />
                                  </div>

                                  <div>
                                    <label style={labelStyle}>
                                      Module Description
                                    </label>
                                    <textarea
                                      value={module.description}
                                      onChange={(e) => handleModuleChange(moduleIndex, 'description', e.target.value)}
                                      rows={2}
                                      style={textareaStyle}
                                    />
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => handleRemoveModule(moduleIndex)}
                                  style={{
                                    marginLeft: SPACING[16],
                                    padding: SPACING[4],
                                    color: COLORS.status.error,
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <TrashIcon style={{ height: '20px', width: '20px' }} />
                                </button>
                              </div>

                              {/* Lessons */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <h5 style={{
                                    fontSize: TYPOGRAPHY.size.base,
                                    fontWeight: TYPOGRAPHY.weight.medium,
                                    color: COLORS.text.primary,
                                  }}>Lessons</h5>
                                  <button
                                    type="button"
                                    onClick={() => handleAddLesson(moduleIndex)}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      padding: `${SPACING[8]} ${SPACING[12]}`,
                                      border: 'none',
                                      fontSize: TYPOGRAPHY.size.xs,
                                      fontWeight: TYPOGRAPHY.weight.medium,
                                      borderRadius: RADIUS,
                                      color: COLORS.bg.panel,
                                      backgroundColor: COLORS.accent.default,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <PlusIcon style={{ marginLeft: '-4px', marginRight: SPACING[4], height: '16px', width: '16px' }} />
                                    Add Lesson
                                  </button>
                                </div>

                                {module.lessons.map((lesson, lessonIndex) => (
                                  <div key={lessonIndex} style={{
                                    border: `1px solid ${COLORS.border.default}`,
                                    borderRadius: RADIUS,
                                    padding: SPACING[16],
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: SPACING[16],
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                                        <div>
                                          <label style={labelStyle}>
                                            Lesson Title
                                          </label>
                                          <input
                                            type="text"
                                            value={lesson.title}
                                            onChange={(e) => handleLessonChange(moduleIndex, lessonIndex, 'title', e.target.value)}
                                            style={inputStyle}
                                            required
                                          />
                                        </div>

                                        <div>
                                          <label style={labelStyle}>
                                            Video URL
                                          </label>
                                          <input
                                            type="url"
                                            value={lesson.video_url}
                                            onChange={(e) => handleLessonChange(moduleIndex, lessonIndex, 'video_url', e.target.value)}
                                            style={inputStyle}
                                          />
                                        </div>

                                        <div>
                                          <label style={labelStyle}>
                                            Summary
                                          </label>
                                          <textarea
                                            value={lesson.summary}
                                            onChange={(e) => handleLessonChange(moduleIndex, lessonIndex, 'summary', e.target.value)}
                                            rows={2}
                                            style={textareaStyle}
                                          />
                                        </div>

                                        <div>
                                          <label style={labelStyle}>
                                            Personal Notes
                                          </label>
                                          <textarea
                                            value={lesson.personal_notes}
                                            onChange={(e) => handleLessonChange(moduleIndex, lessonIndex, 'personal_notes', e.target.value)}
                                            rows={2}
                                            style={textareaStyle}
                                          />
                                        </div>

                                        <div>
                                          <label style={labelStyle}>
                                            Transcript
                                          </label>
                                          <textarea
                                            value={lesson.transcript}
                                            onChange={(e) => handleLessonChange(moduleIndex, lessonIndex, 'transcript', e.target.value)}
                                            rows={3}
                                            style={textareaStyle}
                                          />
                                        </div>

                                        <div>
                                          <label style={labelStyle}>
                                            Quiz
                                          </label>
                                          <textarea
                                            value={lesson.quiz}
                                            onChange={(e) => handleLessonChange(moduleIndex, lessonIndex, 'quiz', e.target.value)}
                                            rows={2}
                                            style={textareaStyle}
                                          />
                                        </div>
                                      </div>

                                      <button
                                        type="button"
                                        onClick={() => handleRemoveLesson(moduleIndex, lessonIndex)}
                                        style={{
                                          marginLeft: SPACING[16],
                                          padding: SPACING[4],
                                          color: COLORS.status.error,
                                          backgroundColor: 'transparent',
                                          border: 'none',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        <TrashIcon style={{ height: '20px', width: '20px' }} />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>

                        {error && (
                          <div style={{
                            borderRadius: RADIUS,
                            backgroundColor: '#FEF2F2',
                            padding: SPACING[16],
                          }}>
                            <div style={{ display: 'flex' }}>
                              <div style={{ flexShrink: 0 }}>
                                <ExclamationCircleIcon style={{ height: '20px', width: '20px', color: COLORS.status.error }} aria-hidden="true" />
                              </div>
                              <div style={{ marginLeft: SPACING[12] }}>
                                <h3 style={{
                                  fontSize: TYPOGRAPHY.size.sm,
                                  fontWeight: TYPOGRAPHY.weight.medium,
                                  color: '#991B1B',
                                }}>{error}</h3>
                              </div>
                            </div>
                          </div>
                        )}

                        {success && (
                          <div style={{
                            borderRadius: RADIUS,
                            backgroundColor: '#F0FDF4',
                            padding: SPACING[16],
                          }}>
                            <div style={{ display: 'flex' }}>
                              <div style={{ flexShrink: 0 }}>
                                <CheckCircleIcon style={{ height: '20px', width: '20px', color: COLORS.status.success }} aria-hidden="true" />
                              </div>
                              <div style={{ marginLeft: SPACING[12] }}>
                                <h3 style={{
                                  fontSize: TYPOGRAPHY.size.sm,
                                  fontWeight: TYPOGRAPHY.weight.medium,
                                  color: '#166534',
                                }}>Course saved successfully!</h3>
                              </div>
                            </div>
                          </div>
                        )}

                        <div style={{
                          marginTop: SPACING[24],
                          display: 'flex',
                          flexDirection: 'row-reverse',
                          gap: SPACING[12],
                        }}>
                          <button
                            type="submit"
                            disabled={loading}
                            style={{
                              display: 'inline-flex',
                              justifyContent: 'center',
                              borderRadius: RADIUS,
                              border: 'none',
                              backgroundColor: COLORS.accent.default,
                              padding: `${SPACING[8]} ${SPACING[16]}`,
                              fontSize: TYPOGRAPHY.size.sm,
                              fontWeight: TYPOGRAPHY.weight.medium,
                              color: COLORS.bg.panel,
                              cursor: loading ? 'not-allowed' : 'pointer',
                              opacity: loading ? 0.6 : 1,
                            }}
                          >
                            {loading ? (
                              <ArrowPathIcon style={{ height: '20px', width: '20px', animation: 'spin 1s linear infinite' }} />
                            ) : (
                              isEditMode ? 'Save Changes' : 'Create Course'
                            )}
                          </button>
                          <button
                            type="button"
                            style={{
                              display: 'inline-flex',
                              justifyContent: 'center',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              backgroundColor: COLORS.bg.panel,
                              padding: `${SPACING[8]} ${SPACING[16]}`,
                              fontSize: TYPOGRAPHY.size.sm,
                              fontWeight: TYPOGRAPHY.weight.medium,
                              color: COLORS.text.secondary,
                              cursor: 'pointer',
                            }}
                            onClick={onClose}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CourseForm;
